<template>
  <div class="bg-base-100 min-h-screen">
    <section class="relative h-[calc(100dvh-var(--navbar-height))] md:h-[560px] lg:h-[640px] flex items-center justify-center overflow-hidden">
      <div class="absolute inset-0 z-0">
        <img :src="heroBackground" alt="Home hero" class="w-full h-full object-cover" />
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"></div>
      </div>

      <div class="relative z-10 w-full max-w-4xl px-4 sm:px-6 md:px-12 text-center">
        <h1 class="text-2xl sm:text-3xl md:text-5xl lg:text-[56px] leading-[1.04] sm:leading-[1.08] md:leading-[1.1] tracking-[-0.02em] font-semibold text-white mb-4 sm:mb-6 md:mb-8">
          {{ inmob_data.home_page_content.title }}
        </h1>
        <SearchBar
          :placeholder="inmob_data.home_page_content.placeholder_search"
          :button-text="inmob_data.home_page_content.search_button_text"
        />
      </div>
    </section>

    <section class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-10 sm:py-14 md:py-20" v-if="recommended_properties">
      <SectionHeader
        title="Propiedades Destacadas"
        subtitle="Explora las opciones más exclusivas seleccionadas por nuestros expertos.">
        <template #action>
          <button class="text-primary font-semibold flex items-center gap-1 hover:underline text-sm md:text-base" @click="$router.push('/search')">
            Ver todas
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </template>
      </SectionHeader>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
        <PropertyCard
          v-for="(property, index) in recommended_properties"
          :key="property.id || index"
          :property="property"
          :show-tag="index === 0"
        />
      </div>
    </section>

    <section class="bg-base-200 py-10 sm:py-14 md:py-20" v-if="new_properties">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <SectionHeader
          title="Últimas Publicaciones"
          subtitle="Sé el primero en ver las nuevas incorporaciones del mercado." />

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
          <div v-if="new_properties[0]" class="md:col-span-2 md:row-span-2 bg-base-100 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all relative overflow-hidden group cursor-pointer"
            @click="$router.push(`/publications/${new_properties[0].id}`)">
            <div class="flex flex-col h-full">
              <div class="mb-4 overflow-hidden rounded-2xl h-64">
                <img :src="new_properties[0].imagenes && new_properties[0].imagenes.length ? new_properties[0].imagenes[0].imagen : ''"
                  :alt="new_properties[0].titulo"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div class="flex-1">
                <UiChip variant="compact" :clickable="false" label="Nuevo ingreso" />
                <div class="text-primary font-semibold text-[40px] leading-none mt-4">{{ new_properties[0].precio }}</div>
                <h4 class="text-[28px] md:text-[20px] font-medium text-base-content mt-1">{{ new_properties[0].titulo }}</h4>
                <p class="text-base-content/70 mt-4 line-clamp-2">{{ new_properties[0].descripcion }}</p>
              </div>
            </div>
          </div>

          <PropertyCard
            v-for="(property, index) in compactLatestProperties"
            :key="property.id || index"
            :property="property"
            variant="compact"
          />

          <div class="md:col-span-2 bg-primary text-primary-content rounded-3xl p-6 sm:p-10 flex flex-col justify-center relative overflow-hidden">
            <div class="relative z-10">
              <h4 class="text-base-content text-xl sm:text-2xl font-bold mb-4">¿Querés vender tu propiedad?</h4>
              <p class="text-base-content/80 mb-6 sm:mb-8 max-w-md">Publicá con nosotros y llegá a más de 100,000 compradores calificados mensualmente.</p>
              <button class="btn btn-primary text-primary px-6 sm:px-8 py-3 rounded-xl font-bold hover:bg-base-100 transition-colors min-h-[44px] border-none">Empezar ahora</button>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="absolute -bottom-10 -right-10 h-44 w-44 opacity-10 rotate-12" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"
                d="M8 7V3m8 4V3M5 8h14M5 8a2 2 0 00-2 2v8a2 2 0 002 2h6m8-12a2 2 0 012 2v8a2 2 0 01-2 2h-2m-5 0h5m-5 0v-5a2 2 0 012-2h1a2 2 0 012 2v5" />
            </svg>
          </div>
        </div>
      </div>
    </section>

    <!-- <section class="py-12 sm:py-16 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
      <h2 class="text-slate-400 uppercase tracking-widest text-[10px] font-bold mb-8 sm:mb-12">Empresas que confían en EstateFlow</h2>
      <div class="flex flex-wrap justify-center items-center gap-6 sm:gap-12 md:gap-24 opacity-40 grayscale">
        <span class="text-2xl font-bold tracking-tighter">RE/MAX</span>
        <span class="text-2xl font-bold tracking-tighter">CENTURY 21</span>
        <span class="text-2xl font-bold tracking-tighter">ZILLOW</span>
        <span class="text-2xl font-bold tracking-tighter">SOTHEBY'S</span>
        <span class="text-2xl font-bold tracking-tighter">KELLER WILLIAMS</span>
      </div>
    </section> -->
  </div>
</template>

<script>
import SearchBar from '@/components/home/SearchBar.vue'
import PropertyCard from '@/components/common/PropertyCard.vue'
import SectionHeader from '@/components/common/ui/SectionHeader.vue'
import UiChip from '@/components/common/ui/UiChip.vue'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'HomeView',
  components: { SearchBar, PropertyCard, SectionHeader, UiChip },
  mounted() {
    this.fetchHomePublications()
  },
  computed: {
    ...mapState({
      inmob_data: state => state.inmob_data,
      recommended_properties: state => state.home.recommended,
      new_properties: state => state.home.latest
    }),
    compactLatestProperties() {
      return this.new_properties ? this.new_properties.slice(1) : []
    },
    heroBackground() {
      if (!this.inmob_data || !this.inmob_data.home_page_content || !this.inmob_data.home_page_content.background_image) {
        return ''
      }
      return `${process.env.VUE_APP_API_BASE_URL}${this.inmob_data.home_page_content.background_image}`
    },
  },
  methods: {
    ...mapActions({
      fetchHomePublications: 'FETCH_HOME_PUBLICATIONS'
    }),
  }
}
</script>
